import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import { LogOut, Sun, Moon, Boxes, Presentation } from 'lucide-react';
import { ProfileModal } from '../components/ProfileModal';
import { InventoryTab } from '../components/inventory/InventoryTab';
import { useStudioAccess } from '../components/proposal_studio/useStudioAccess';

// BD Transactions: read-only access to the Inventory module, plus Proposal Studio
// for members of the studio's Transactions team. The backend enforces this
// (auth.py role-scope guard); this page simply offers no other modules.
const BDTransactionsDashboard = () => {
  const { user, api, logout } = useAuth();
  const studioAccess = useStudioAccess(api);
  const { isDark, toggleTheme } = useTheme();
  const [venues, setVenues] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    api.get('/venues')
      .then(r => setVenues(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVenues([]));
  }, [api]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0b0d]' : 'bg-[#f7f3e8]'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-[#f7f3e8] border-[#e5dfd0]'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="Clayworks" className="h-8 sm:h-9 w-auto" />
            <div>
              <h1 className="font-heading text-lg sm:text-xl font-bold tracking-tight">BD Transactions</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Toggle theme">
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setShowProfileModal(true)} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Profile" data-testid="sidebar-profile-btn">
              {user?.photo_url ? (
                <img src={user.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">{user?.name?.charAt(0)}</div>
              )}
            </button>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation — Inventory, plus Proposal Studio for studio members (it lives on its own page) */}
      <div className={`border-b ${isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center gap-1">
          <span className="shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 border-primary text-primary" data-testid="bdt-tab-inventory">
            <Boxes className="w-4 h-4 inline mr-1.5" />
            Inventory
          </span>
          {studioAccess && (
            <Link
              to="/proposal-studio"
              className="shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 border-transparent text-muted-foreground hover:text-foreground"
              data-testid="bdt-tab-proposal-studio"
            >
              <Presentation className="w-4 h-4 inline mr-1.5" />
              Proposal Studio
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 space-y-6 sm:space-y-8">
        <InventoryTab api={api} user={user} venues={venues.filter(v => v.is_active !== false)} />
      </div>

      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        api={api}
        onProfileUpdate={() => window.location.reload()}
      />
    </div>
  );
};

export default BDTransactionsDashboard;
